/**
 * Where a person lands after signing out from a firm's snapshot form
 * (crossmarc/steward-app#2158).
 *
 * Signing out ends the Cognito session at its `/logout` endpoint, and Cognito only sends a
 * person back to a registered sign-out address. The one registered per host is
 * `{origin}/login` (crossmarc-auth `deploy/lib/app-client.ts`, read 2026-10-07), so that is
 * where every sign-out returns, the snapshot form's included. A prospect on a firm's form
 * should not be left on the main app's login page, so the form leaves a note here before it
 * signs out, and `/login` reads the note and goes straight to the form's cover.
 *
 * The full sign-out still runs (`useAuth().signOut()`): nothing here shortens it.
 *
 * What the note may hold, and nothing else: the path of a snapshot form on this same host,
 * `/intake` or `/intake/{firmSlug}`. It is checked when written and again when read, so text
 * placed in storage by hand cannot send `/login` anywhere else. It is read once and removed,
 * and it is ignored after {@link MAX_AGE_MS}, so a sign-out that never came back does not
 * redirect a later, unrelated visit to `/login` in the same tab.
 */

import { sameOriginPath } from './returnPath';

const KEY = 'steward.signout.returnTo.v1';

/** Long enough for the round trip through Cognito on a slow connection, and not much longer. */
const MAX_AGE_MS = 2 * 60 * 1000;

const SNAPSHOT_FORM_PATH = /^\/intake(\/[a-z0-9][a-z0-9-]{0,63})?$/;

export function isSnapshotFormPath(value: unknown): value is string {
  return typeof value === 'string' && SNAPSHOT_FORM_PATH.test(value);
}

/** Called just before signing out. A path that is not a snapshot form's is not written. */
export function rememberSignOutReturn(path: string, now: number = Date.now()): void {
  try {
    if (!isSnapshotFormPath(path)) {
      sessionStorage.removeItem(KEY);
      return;
    }
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Storage refused: the person lands on /login, which is where sign-out went before.
  }
}

/** Called by `/login`. Returns the form's path once, or `null`; the note is removed either way. */
export function takeSignOutReturn(now: number = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const at = parsed['at'];
    if (typeof at !== 'number' || now - at < 0 || now - at > MAX_AGE_MS) return null;
    const path = parsed['path'];
    return isSnapshotFormPath(path) ? path : null;
  } catch {
    return null;
  }
}

/**
 * "Continue with Google once this sign-out has come back" (crossmarc/steward-app#2408).
 *
 * The snapshot form's "Please sign in with Google again" answer needs a NEW Google sign-in
 * for a login whose ID token was spent. It ends the old one with the app's full sign-out
 * first (`useAuth().signOut()`: the stored tokens, the refresh token's revocation, and the
 * sign-in service's own session at its `/logout`). Ending only the stored tokens would leave
 * that service's session cookie alive with nothing in the browser to show for it, and on a
 * shared browser the next person to press a sign-in button could be let in as this login.
 *
 * The full sign-out returns to `/login` (see the top of this file), so this second note asks
 * `/login` to start the Google sign-in, back to the form, as soon as it finds nobody signed
 * in. It follows the same rules as the note above, separately stored so that nothing which
 * reads the form's return path can start a sign-in by accident:
 *
 *  - Only a snapshot form's path is written, and the path is checked again when read, both
 *    as a snapshot form's path and as a path on this origin (`sameOriginPath`).
 *  - `/login` is its only reader. It is removed BEFORE anything is decided from it, whatever
 *    it holds, so it is acted on at most once and can never loop.
 *  - It is ignored after {@link MAX_AGE_MS}, so a sign-out that never came back cannot start
 *    a Google sign-in on a later, unrelated visit to `/login` in the same tab.
 *  - `/login` does not act on it when somebody is signed in.
 */
const GOOGLE_KEY = 'steward.signout.thenGoogle.v1';

/** Called just before the full sign-out. A path that is not a snapshot form's is not written. */
export function rememberGoogleAfterSignOut(path: string, now: number = Date.now()): void {
  try {
    if (!isSnapshotFormPath(path) || sameOriginPath(path) !== path) {
      sessionStorage.removeItem(GOOGLE_KEY);
      return;
    }
    sessionStorage.setItem(GOOGLE_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Storage refused: the person lands on the form's sign-in step instead (the note above).
  }
}

/** The sign-out did not happen (it threw): the request is withdrawn. */
export function forgetGoogleAfterSignOut(): void {
  try {
    sessionStorage.removeItem(GOOGLE_KEY);
  } catch {
    // Nothing stored, then.
  }
}

/**
 * Called by `/login`, once, as it mounts. Returns the form's path to start the Google sign-in
 * for, or `null`; the note is removed first, either way.
 */
export function takeGoogleAfterSignOut(now: number = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(GOOGLE_KEY);
    sessionStorage.removeItem(GOOGLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const at = parsed['at'];
    if (typeof at !== 'number' || now - at < 0 || now - at > MAX_AGE_MS) return null;
    const path = parsed['path'];
    return isSnapshotFormPath(path) && sameOriginPath(path) === path ? path : null;
  } catch {
    return null;
  }
}
